import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Form, Input, message, Avatar, Switch, Modal, Checkbox } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import NewsletterSubscription from '../components/NewsletterSubscription';
import LmHeader from '../components/LmHeader.jsx';
import LmFooter from '../components/LmFooter.jsx';

const ClientProfile = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Настройки уведомлений
  const [notifications, setNotifications] = useState({
    productEvents: true,
    reminders: true,
    brandNews: false
  });

  // Состояние для аватара
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Удаление аккаунта
  const [deleteIncludePersonal, setDeleteIncludePersonal] = useState(false);
  const [keepNameInHistory, setKeepNameInHistory] = useState(false);
  const [keepPublicPhotos, setKeepPublicPhotos] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deletionRequest, setDeletionRequest] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [deletionBusy, setDeletionBusy] = useState(false);

  useEffect(() => {
    loadClientProfile();
  }, []);

  const apiHeaders = () => {
    const token = localStorage.getItem('clientToken');
    return { Authorization: `Bearer ${token}` };
  };

  const loadDeletionRequest = async () => {
    try {
      const response = await axios.get('https://limitverify.ru:5000/api/client/delete-account', {
        headers: apiHeaders()
      });
      setDeletionRequest(response.data.request || null);
    } catch (_) {
      setDeletionRequest(null);
    }
  };

  const loadClientProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('clientToken');
      if (!token) {
        navigate('/enter');
        return;
      }

      const response = await axios.get('https://limitverify.ru:5000/api/client/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const clientData = response.data.client;
      setClient(clientData);
      localStorage.setItem('clientData', JSON.stringify(clientData));
      form.setFieldsValue({
        firstName: clientData.firstName || '',
        lastName: clientData.lastName || '',
        email: clientData.email || '',
        city: clientData.city || '',
        country: clientData.country || '',
        comment: clientData.comment || ''
      });

      // Загружаем настройки уведомлений
      setNotifications({
        productEvents: clientData.notifications?.productEvents ?? true,
        reminders: clientData.notifications?.reminders ?? true,
        brandNews: clientData.notifications?.brandNews ?? false
      });

      // Загружаем аватар
      if (clientData.avatar) {
        setAvatarPreview(clientData.avatar);
      } else {
        setAvatarPreview(null);
      }

      await loadDeletionRequest();
    } catch (err) {
      console.error('Ошибка загрузки профиля:', err);
      setError('Ошибка загрузки профиля');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationChange = async (name, checked) => {
    try {
      // Сразу обновляем состояние
      setNotifications(prev => ({
        ...prev,
        [name]: checked
      }));

      // Сохраняем в базе данных
      const token = localStorage.getItem('clientToken');
      await axios.put('https://limitverify.ru:5000/api/client/profile', {
        firstName: client?.firstName,
        lastName: client?.lastName,
        email: client?.email,
        city: client?.city,
        country: client?.country,
        comment: client?.comment,
        avatar: client?.avatar,
        notifications: {
          ...notifications,
          [name]: checked
        }
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Обновляем данные клиента
      setClient(prevClient => ({
        ...prevClient,
        notifications: {
          ...prevClient.notifications,
          [name]: checked
        }
      }));
    } catch (err) {
      console.error('Ошибка сохранения уведомления:', err);
      message.error('Ошибка сохранения настройки уведомления');

      // Возвращаем предыдущее состояние при ошибке
      setNotifications(prev => ({
        ...prev,
        [name]: !checked
      }));
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Проверяем размер файла (5MB)
      if (file.size > 5 * 1024 * 1024) {
        message.error('Размер файла не должен превышать 5MB');
        return;
      }

      // Проверяем тип файла
      if (!file.type.startsWith('image/')) {
        message.error('Выберите изображение');
        return;
      }

      try {
        setError('');
        setSuccess('');

        // Загружаем аватар в S3
        const avatarUrl = await uploadAvatar(file);
        setAvatarPreview(avatarUrl);
        setAvatar(null); // Сбрасываем файл, так как уже загружен

        // Сразу сохраняем аватар в профиле
        await saveAvatarToProfile(avatarUrl);

        message.success('Аватар загружен и сохранен!');
      } catch (error) {
        console.error('Error uploading avatar:', error);
        message.error('Ошибка загрузки аватара');
        // Сбрасываем превью при ошибке
        setAvatarPreview(client?.avatar || null);
      }
    }
  };

  const uploadAvatar = async (file) => {
    try {
      setUploadingAvatar(true);
      const formData = new FormData();
      formData.append('avatar', file);

      const token = localStorage.getItem('clientToken');
      const response = await axios.post('https://limitverify.ru:5000/api/upload/client/avatar', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.url;
    } catch (err) {
      console.error('Ошибка загрузки аватара:', err);
      throw new Error('Ошибка загрузки аватара');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const saveAvatarToProfile = async (avatarUrl) => {
    try {
      const token = localStorage.getItem('clientToken');

      // Получаем текущие данные формы
      const formValues = form.getFieldsValue();

      // Отправляем все обязательные поля + аватар
      await axios.put('https://limitverify.ru:5000/api/client/profile', {
        firstName: formValues.firstName || client?.firstName,
        lastName: formValues.lastName || client?.lastName,
        email: formValues.email || client?.email,
        city: formValues.city || client?.city,
        country: formValues.country || client?.country,
        comment: formValues.comment || client?.comment,
        avatar: avatarUrl,
        notifications: notifications
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Обновляем данные клиента
      setClient(prevClient => ({
        ...prevClient,
        avatar: avatarUrl
      }));
    } catch (err) {
      console.error('Ошибка сохранения аватара в профиле:', err);
      throw new Error('Ошибка сохранения аватара в профиле');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const values = await form.validateFields();

      const token = localStorage.getItem('clientToken');
      const response = await axios.put('https://limitverify.ru:5000/api/client/profile', {
        ...values,
        notifications: notifications
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      message.success('Профиль обновлен успешно!');
      setClient(response.data.client);
      await loadClientProfile(); // Перезагружаем данные
    } catch (err) {
      console.error('Ошибка сохранения профиля:', err);
      if (err.response?.data?.error) {
        message.error(err.response.data.error);
      } else {
        message.error('Ошибка при обновлении профиля');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientData');
    navigate('/enter');
  };

  const handleConfirmDelete = async () => {
    if (!deleteIncludePersonal) {
      message.warning('Отметьте «Удалить мои личные данные»');
      return;
    }
    Modal.confirm({
      title: 'Запросить удаление аккаунта?',
      content: 'Мы свяжемся с вами для подтверждения. Удаление выполнит администратор после вашего кода из письма.',
      okText: 'Отправить заявку',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        setDeletionBusy(true);
        try {
          const response = await axios.post('https://limitverify.ru:5000/api/client/delete-account', {
            deletePersonal: true,
            keepNameInHistory,
            keepPublicPhotos,
            reason: deleteReason
          }, { headers: apiHeaders() });
          setDeletionRequest(response.data.request || null);
          message.success(response.data.message || 'Заявка создана. Проверьте почту.');
        } catch (err) {
          if (err.response?.status === 409 && err.response?.data?.request) {
            setDeletionRequest(err.response.data.request);
            message.info('Заявка уже существует');
          } else {
            message.error(err.response?.data?.error || 'Не удалось создать заявку');
          }
        } finally {
          setDeletionBusy(false);
        }
      }
    });
  };

  const handleConfirmCode = async () => {
    if (!confirmCode.trim()) {
      message.warning('Введите код из письма');
      return;
    }
    setDeletionBusy(true);
    try {
      const response = await axios.post(
        'https://limitverify.ru:5000/api/client/delete-account/confirm-code',
        { code: confirmCode.trim() },
        { headers: apiHeaders() }
      );
      message.success(response.data.message || 'Подтверждено');
      setConfirmCode('');
      await loadDeletionRequest();
    } catch (err) {
      message.error(err.response?.data?.error || 'Неверный код');
    } finally {
      setDeletionBusy(false);
    }
  };

  const deletionStatusLabel = {
    pending: 'Ожидает обработки',
    awaiting_client: 'Ожидает ваш код из письма',
    client_confirmed: 'Подтверждено вами — ждёт администратора',
    completed: 'Выполнено',
    cancelled: 'Отменено'
  };

  if (loading) {
    return (
      <div className="lm-page">
        <main className="lm-content lm-content--center">
          <span className="lm-text-2 lm-muted">Загрузка профиля...</span>
        </main>
      </div>
    );
  }

  return (
    <div className="lm-page">
      <LmHeader
        logoTo="/cabinet"
        actions={[
          { label: 'Личный кабинет', onClick: () => navigate('/cabinet') },
          { label: 'Выйти', onClick: handleLogout }
        ]}
      />

      <main className="lm-content">
        <div className="lm-auth__head" style={{ alignItems: 'center', textAlign: 'center' }}>
          <h1 className="lm-h1">Мой профиль</h1>
          <span className="lm-auth__sub">Управление профилем клиента</span>
        </div>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Боковая панель: аватар + уведомления */}
          <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 24 }}>
            <section className="lm-section" style={{ alignItems: 'center' }}>
              <h3 className="lm-h3" style={{ alignSelf: 'flex-start' }}>Аватар</h3>
              <Avatar
                size={160}
                src={avatarPreview || client?.avatar}
                icon={<UserOutlined />}
                style={{ border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-control)' }}
              />
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                disabled={uploadingAvatar}
                id="avatar-upload"
                style={{ display: 'none' }}
              />
              <label htmlFor="avatar-upload" className="lm-btn" style={{ cursor: uploadingAvatar ? 'not-allowed' : 'pointer' }}>
                {uploadingAvatar ? 'Загрузка...' : 'Выбрать фото'}
              </label>
              <span className="lm-descriptor lm-muted">Максимальный размер: 5MB</span>
            </section>

            <section className="lm-section">
              <h3 className="lm-h3">Уведомления</h3>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span className="lm-text-3">События с изделиями</span>
                <Switch checked={notifications.productEvents} onChange={(checked) => handleNotificationChange('productEvents', checked)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span className="lm-text-3">Напоминания</span>
                <Switch checked={notifications.reminders} onChange={(checked) => handleNotificationChange('reminders', checked)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span className="lm-text-3">Новости бренда</span>
                <Switch checked={notifications.brandNews} onChange={(checked) => handleNotificationChange('brandNews', checked)} />
              </div>
            </section>

            <NewsletterSubscription />
          </div>

          {/* Основная информация + удаление */}
          <div style={{ flex: 2, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 24 }}>
            <section className="lm-section lm-fields">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h3 className="lm-h3">Основная информация</h3>
                <button type="button" className="lm-btn" onClick={handleSave} disabled={saving}>
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>

              <Form form={form} layout="vertical" requiredMark={false} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Form.Item
                    name="firstName"
                    label="Имя"
                    style={{ marginBottom: 0, flex: 1, minWidth: 140 }}
                    rules={[{ required: true, message: 'Пожалуйста, введите имя!' }]}
                  >
                    <Input placeholder="Имя" />
                  </Form.Item>
                  <Form.Item
                    name="lastName"
                    label="Фамилия"
                    style={{ marginBottom: 0, flex: 1, minWidth: 140 }}
                    rules={[{ required: true, message: 'Пожалуйста, введите фамилию!' }]}
                  >
                    <Input placeholder="Фамилия" />
                  </Form.Item>
                </div>

                <Form.Item name="email" label="Email" style={{ marginBottom: 0 }}>
                  <Input disabled />
                </Form.Item>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Form.Item name="city" label="Город" style={{ marginBottom: 0, flex: 1, minWidth: 140 }}>
                    <Input placeholder="Например: Москва" />
                  </Form.Item>
                  <Form.Item name="country" label="Страна" style={{ marginBottom: 0, flex: 1, minWidth: 140 }}>
                    <Input placeholder="Например: Россия" />
                  </Form.Item>
                </div>
              </Form>
            </section>

            <section className="lm-section lm-fields">
              <h3 className="lm-h3">Удалить аккаунт</h3>
              <span className="lm-text-3 lm-muted">
                Вы подаёте заявку → на email приходит код/ссылка → вы подтверждаете → поддержка завершает удаление.
                Менеджер код не вводит; при звонке он может подтвердить за вас после устного согласия.
              </span>

              {deletionRequest && !['completed', 'cancelled'].includes(deletionRequest.status) ? (
                <>
                  <span className="lm-text-3">
                    Статус: {deletionStatusLabel[deletionRequest.status] || deletionRequest.status}
                  </span>
                  {deletionRequest.status === 'awaiting_client' && (
                    <>
                      <div className="lm-field">
                        <span className="lm-field__label">Код из письма</span>
                        <input
                          type="text"
                          className="lm-field__input"
                          placeholder="6 цифр"
                          value={confirmCode}
                          onChange={(e) => setConfirmCode(e.target.value)}
                          maxLength={6}
                        />
                      </div>
                      <button
                        type="button"
                        className="lm-btn lm-btn--danger"
                        onClick={handleConfirmCode}
                        disabled={deletionBusy}
                      >
                        {deletionBusy ? 'Проверка...' : 'Подтвердить код'}
                      </button>
                    </>
                  )}
                  {deletionRequest.status === 'client_confirmed' && (
                    <span className="lm-text-3 lm-muted">
                      С вами свяжутся по email/телефону при необходимости. Вход будет заблокирован после финального удаления.
                    </span>
                  )}
                </>
              ) : (
                <>
                  <Checkbox
                    checked={deleteIncludePersonal}
                    onChange={(e) => setDeleteIncludePersonal(e.target.checked)}
                  >
                    <span className="lm-text-3">Удалить мои личные данные</span>
                  </Checkbox>
                  <Checkbox
                    checked={keepNameInHistory}
                    onChange={(e) => setKeepNameInHistory(e.target.checked)}
                  >
                    <span className="lm-text-3">Оставить имя в истории владения изделиями</span>
                  </Checkbox>
                  <Checkbox
                    checked={keepPublicPhotos}
                    onChange={(e) => setKeepPublicPhotos(e.target.checked)}
                  >
                    <span className="lm-text-3">Сохранить мои публичные фото для истории бренда</span>
                  </Checkbox>
                  <div className="lm-field">
                    <span className="lm-field__label">Причина (опционально)</span>
                    <input
                      type="text"
                      className="lm-field__input"
                      placeholder="Причина (опционально)"
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="lm-btn lm-btn--danger"
                    onClick={handleConfirmDelete}
                    disabled={!deleteIncludePersonal || deletionBusy}
                    style={{ opacity: deleteIncludePersonal ? 1 : 0.5 }}
                  >
                    {deletionBusy ? 'Отправка...' : 'Запросить удаление'}
                  </button>
                </>
              )}
            </section>
          </div>
        </div>
      </main>

      <LmFooter />
    </div>
  );
};

export default ClientProfile;
